import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/system";
import { useNavigate } from "react-router-dom";
import CustomStyledMenu from "../../../../components/Menu/CustomStyledMenu";
import SearchField from "../../../../components/InputFields/SearchField";
import Checkbox from "../../../../components/Buttons/Checkbox/Checkbox";
import { Button } from "../../../../components/Buttons";
import EventEmitter from "../../../../utils/emitter/EventEmitter";
import {
  EMITTER_ERROR,
  EMITTER_SUCCESS,
} from "../../../../utils/constants";
import { CampaignSettingsEndpoints } from "../../../../utils/constants/httpConstants";
import { postRequest } from "../../../../Apis/apiWrapper";
import { getErrorMessage } from "../../../../utils/helpers";
import { STRINGS } from "../../../Marketing/strings";
import { styles } from "./styles";

// Add/move calls go out one mailbox at a time, so cap how many run together.
const ADD_BATCH_SIZE = 5;

// mailboxes  : [{ id, email, provider }]
// mailboxToGroup : Map<account_id, { groupId, groupName } | null>
// targetGroupId  : group._id we're adding into
const AddMailboxesMenu = ({
  anchorEl,
  open,
  onClose,
  onAdded,
  mailboxes = [],
  mailboxToGroup,
  targetGroupId,
}) => {
  const classes = styles();
  const navigate = useNavigate();

  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [isAdding, setIsAdding] = useState(false);

  useEffect(() => {
    if (open) {
      setSearch("");
      setSelected([]);
    }
  }, [open]);

  // Pool = all mailboxes NOT already in the target group.
  const pool = useMemo(() => {
    if (!targetGroupId) return [];
    return mailboxes
      .filter((m) => {
        const cur = mailboxToGroup?.get?.(m.id);
        return !cur || cur.groupId !== targetGroupId;
      })
      .map((m) => {
        const cur = mailboxToGroup?.get?.(m.id);
        return {
          id: m.id,
          email: m.email,
          alias: m.alias || null, // send-as alias, display-only
          provider: m.provider,
          fromGroupId: cur?.groupId || null,
          fromGroupName: cur?.groupName || null,
        };
      });
  }, [mailboxes, mailboxToGroup, targetGroupId]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return pool;
    return pool.filter(
      (m) =>
        (m.email || "").toLowerCase().includes(q) ||
        (m.alias || "").toLowerCase().includes(q),
    );
  }, [pool, search]);

  const toggle = (id) => {
    setSelected((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  // Select-all acts on what's visible, so it respects the search filter.
  const filteredIds = useMemo(() => filtered.map((m) => m.id), [filtered]);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.includes(id));
  const someFilteredSelected =
    !allFilteredSelected && filteredIds.some((id) => selected.includes(id));

  const toggleSelectAll = () => {
    setSelected((prev) =>
      allFilteredSelected
        ? prev.filter((id) => !filteredIds.includes(id))
        : Array.from(new Set([...prev, ...filteredIds])),
    );
  };

  // Per-id: try /group/add-email first. If the server says the mailbox is
  // already in another group, fall back to /group/move-email (server
  // resolves the source group server-side, so we don't need to know it).
  const addOrMove = async (account_id) => {
    try {
      return await postRequest(
        navigate,
        CampaignSettingsEndpoints.mailboxGroupAddEmail,
        { group_id: targetGroupId, account_id },
      );
    } catch (err) {
      const msg = err?.data?.message || "";
      if (/another group|use move/i.test(msg)) {
        return postRequest(
          navigate,
          CampaignSettingsEndpoints.mailboxGroupMoveEmail,
          { to_group_id: targetGroupId, account_id },
        );
      }
      throw err;
    }
  };

  const handleSubmit = async () => {
    if (!selected.length || !targetGroupId) return;
    try {
      setIsAdding(true);
      const picked = pool.filter((m) => selected.includes(m.id));
      // There is no bulk endpoint, so a select-all could fire dozens of calls
      // at once. Send them in small batches instead.
      const results = [];
      for (let i = 0; i < picked.length; i += ADD_BATCH_SIZE) {
        const batch = picked.slice(i, i + ADD_BATCH_SIZE);
        // eslint-disable-next-line no-await-in-loop
        const batchResults = await Promise.allSettled(
          batch.map((m) => addOrMove(m.id)),
        );
        results.push(...batchResults);
      }
      const successCount = results.filter(
        (r) => r.status === "fulfilled",
      ).length;
      const failures = results.filter((r) => r.status === "rejected");

      if (successCount > 0) {
        EventEmitter.emit(
          EMITTER_SUCCESS,
          `${successCount} mailbox${successCount > 1 ? "es" : ""} added`,
        );
      }
      if (failures.length > 0) {
        const first = failures[0].reason;
        EventEmitter.emit(
          EMITTER_ERROR,
          `${failures.length} couldn't be added: ${getErrorMessage(
            first?.data?.data?.errors || {},
            first?.data?.message,
          )}`,
        );
      }
      onAdded?.();
      onClose?.();
    } catch (err) {
      EventEmitter.emit(
        EMITTER_ERROR,
        getErrorMessage(err?.data?.data?.errors || {}, err?.data?.message),
      );
    } finally {
      setIsAdding(false);
    }
  };

  return (
    <CustomStyledMenu
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      width="360px"
      noMargin
    >
      <Box className={classes.mgMoveMenu}>
        <Box className={classes.mgMoveMenuHeader}>
          {STRINGS.ADD_MAILBOXES_TITLE}
        </Box>
        <Box className={classes.mgMoveMenuSearch}>
          <SearchField
            autoComplete="off"
            size={"sm36"}
            placeholder={STRINGS.MAILBOXES_SEARCH_PLACEHOLDER}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </Box>
        {filtered.length > 0 ? (
          <Box className={classes.mgAddMenuSelectAll}>
            <Checkbox
              label={`${
                allFilteredSelected ? STRINGS.DESELECT : STRINGS.SELECT_ALL
              } (${filtered.length})`}
              checked={allFilteredSelected}
              partial={someFilteredSelected}
              onChange={toggleSelectAll}
              noMargin
            />
          </Box>
        ) : null}
        <Box className={classes.mgMoveMenuList}>
          {filtered.length === 0 ? (
            <Box className={classes.mgMoveMenuEmpty}>
              {mailboxes.length === 0
                ? STRINGS.ADD_MAILBOXES_NO_INBOXES
                : pool.length === 0
                ? STRINGS.ADD_MAILBOXES_EMPTY
                : STRINGS.NO_MAILBOXES_MATCH}
            </Box>
          ) : (
            filtered.map((m) => {
              const isChecked = selected.includes(m.id);
              return (
                <Box
                  key={m.id}
                  className={classes.mgAddMenuItem}
                  onClick={() => toggle(m.id)}
                >
                  <Checkbox
                    checked={isChecked}
                    onChange={() => toggle(m.id)}
                    noMargin
                  />
                  <Box className={classes.mgAddMenuItemMain}>
                    <span
                      className={classes.mgAddMenuItemEmail}
                      title={m.alias ? `Mailbox: ${m.email}` : m.email}
                    >
                      {m.alias || m.email}
                    </span>
                    <span className={classes.mgAddMenuItemMeta}>
                      {m.provider || ""}
                      <span
                        className={classes.mgGroupTag}
                        title={m.fromGroupName || STRINGS.UNGROUPED}
                      >
                        {m.fromGroupName
                          ? `${STRINGS.CURRENT_GROUP_PREFIX} ${m.fromGroupName}`
                          : STRINGS.UNGROUPED}
                      </span>
                    </span>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
        <Box className={classes.mgMoveMenuFooter}>
          <Box />
          <Box className={classes.mgMoveMenuFooterActions}>
            <Button
              label={STRINGS.CANCEL}
              variant={"ghost"}
              size={"sm36"}
              onClick={onClose}
            />
            <Button
              label={
                selected.length > 0
                  ? `${STRINGS.ADD} (${selected.length})`
                  : STRINGS.ADD
              }
              variant={"primary"}
              size={"sm36"}
              onClick={handleSubmit}
              disabled={selected.length === 0}
              isLoading={isAdding}
            />
          </Box>
        </Box>
      </Box>
    </CustomStyledMenu>
  );
};

export default AddMailboxesMenu;
